import { useState } from 'react';

import { Button, TextField, Checkbox } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useStyles } from './styles/LoginForm.styles';
import { validateEmail, validatePassword } from '@utils/validate';
import { signIn } from '@api/user';
import { store } from '@features/redux/store';
import { setIdToken, setPersist, setRefreshToken } from '@features/redux/slices/authSlice';
import { useAlert } from '@hooks/useAlert';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '@consts/routes';
import { useSetLoading } from '@hooks/useSetLoading';

export const LoginForm = () => {
    const classes = useStyles();
    const { setAlert } = useAlert();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [keepSigned, setKeepSigned] = useState(false);
    const { setLoading } = useSetLoading();

    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const updateEmail = (email: string) => {
        const { message } = validateEmail(email);

        setEmailError(message);
        setEmail(email);
    };

    const updatePassword = (password: string) => {
        const { message } = validatePassword(password);

        setPasswordError(message);
        setPassword(password);
    };

    const handleSignIn = async () => {
        setLoading(true);
        try {
            const { data } = await signIn(email, password);
            const { IdToken, RefreshToken } = data.AuthenticationResult || data;
            store.dispatch(setRefreshToken(RefreshToken));
            store.dispatch(setIdToken(IdToken));
            store.dispatch(setPersist(keepSigned));
            navigate(AppRoutes.Insights);
        } catch (err) {
            setAlert('Login failed! Check your credentials and try again!', 'error');
        } finally {
            setLoading(false);
        }
    };

    /**  Autofill HTML Spec:
     * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill-field
     * */
    return (
        <form
            className={classes.root}
            onSubmit={(e) => {
                e.preventDefault();
                handleSignIn();
            }}>
            <div className={classes.fields}>
                <TextField
                    id='email'
                    fullWidth
                    error={Boolean(emailError)}
                    helperText={emailError ? emailError : ''}
                    label='Email address'
                    name='email'
                    onChange={(e) => updateEmail(e.target.value)}
                    type='email'
                    value={email}
                    variant='outlined'
                    autoComplete='email'
                />

                <TextField
                    id='password'
                    fullWidth
                    error={Boolean(passwordError)}
                    helperText={passwordError ? passwordError : ''}
                    label='Password'
                    name='password'
                    onChange={(e) => updatePassword(e.target.value)}
                    type='password'
                    value={password}
                    variant='outlined'
                    autoComplete='current-password'
                />
            </div>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={keepSigned}
                        onChange={(e) => setKeepSigned(e.target.checked)}
                        name='keepSignedIn'
                        color='primary'
                    />
                }
                label='Keep me signed in'
            />

            <Button
                className={classes.submitButton}
                disabled={Boolean(emailError) || Boolean(passwordError) || !email || !password}
                size='large'
                type='submit'
                variant='contained'>
                Sign in
            </Button>
        </form>
    );
};
